import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { createResource, useResource, type RouteResource } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { safeCloneResource } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import type { GpEndDateData } from '@jira/servicedesk__premium-landing-page/src/getGpEndDateDetails.tsx';
import type {
	AnnouncementBannerDisplayState,
	WarningType,
} from './AnnouncementBannerDisplayState.types.tsx';
import { JSM_GRACE_PERIOD_ANNOUNCEMENT_USER_STATE_STORAGE_KEY } from './fallbackGpEndDate.constants.tsx';

type AccountId = string;

export const jsmGracePeriodAnnouncementDisplayStateResource: RouteResource<AnnouncementBannerDisplayState> =
	safeCloneResource(
		createResource<AnnouncementBannerDisplayState>({
			type: 'JSM_GRACE_PERIOD_ANNOUNCEMENT_DISPLAY_STATE',
			getKey: () => 'JSM_GRACE_PERIOD_ANNOUNCEMENT_DISPLAY_STATE',
			getDataLoader: () =>
				import(
					/*
					webpackChunkName: "async-resource-jsm-grace-period-announcements-display-state",
					jiraSpaEntry: "async-resource-jsm-grace-period-announcements-display-state"
				*/
					'./getAnnouncementBannerDisplayStateData.tsx'
				),
			maxAge: Infinity,
		}),
		{
			get isPageShellCritical() {
				return !fg('jira_ssr_demote_optional_critical_resources');
			},
		},
	);

const getNewAnnouncementDismissState = (
	bannerType: WarningType,
	baseDisplayState: AnnouncementBannerDisplayState,
): AnnouncementBannerDisplayState => {
	// T90 dismiss state is persisted under `hideT90BannerNew`. The legacy
	// `hideT90Banner` key is retired: it only ever applied to grace periods
	// ending before Nov 5, 2025, which have all since expired.
	const getHideBannerKey = (warning: WarningType) =>
		warning === 'T90' ? 'hideT90BannerNew' : `hide${warning}Banner`;
	const updatedState = {
		...baseDisplayState,
		[getHideBannerKey(bannerType)]: true,
	};

	return updatedState;
};

export const useJsmGracePeriodAnnouncementDisplayStateResource = (
	accountId: AccountId | null,
):
	| {
			loading: boolean;
			displayState: {
				hideT7Banner: boolean;
				hideT30Banner: boolean;
				hideT90BannerNew: boolean;
			};

			dismissAnnouncement: (
				baseState: AnnouncementBannerDisplayState,
				bannerType: WarningType,
				analyticsEvent: UIAnalyticsEvent,
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			) => Promise<any> | undefined;
	  }
	| {
			displayState: AnnouncementBannerDisplayState;
			loading: false;

			dismissAnnouncement: (
				baseState: AnnouncementBannerDisplayState,
				bannerType: WarningType,
				analyticsEvent: UIAnalyticsEvent,
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			) => Promise<any> | undefined;
	  } => {
	const { data, loading, error, refresh, update } = useResource(
		jsmGracePeriodAnnouncementDisplayStateResource,
	);
	const loadingRef = useRef(false);
	const errorRef = useRef<unknown>();
	const [userPropertiesError, setUserPropertiesError] = useState<Error>();
	// oxlint-disable-next-line react/refs
	errorRef.current = userPropertiesError || error;

	const dismissAnnouncement = useCallback(
		(
			baseState: AnnouncementBannerDisplayState,
			bannerType: WarningType,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			update(() => getNewAnnouncementDismissState(bannerType, baseState));

			if (accountId) {
				if (loadingRef.current) {
					const err = new Error('User properties is loading');
					fireErrorAnalytics({
						meta: {
							id: 'useJsmGracePeriodAnnouncementResource-update',
							packageName: 'jiraRouterResourcesJsmGracePeriodAnnouncements',
							teamName: 'Seqouia',
						},
						error: err,
						sendToPrivacyUnsafeSplunk: true,
					});
					setUserPropertiesError(err);
					return Promise.reject(err);
				}

				loadingRef.current = true;
				return setUserProperties(
					accountId,
					JSM_GRACE_PERIOD_ANNOUNCEMENT_USER_STATE_STORAGE_KEY,
					JSON.stringify(getNewAnnouncementDismissState(bannerType, baseState)),
				)
					.catch((err) => {
						setUserPropertiesError(new Error('Error occured while setting user properties'));
						fireErrorAnalytics({
							meta: {
								id: 'useJsmGracePeriodAnnouncementResource-update',
								packageName: 'jiraRouterResourcesJsmGracePeriodAnnouncements',
								teamName: 'Sequoia',
							},
							error: err,
							sendToPrivacyUnsafeSplunk: true,
						});
					})
					.finally(() => {
						// SQA-804: Add more typeOfUserEngagement attributes when we release the banner to low engaged GPC
						fireUIAnalytics(analyticsEvent, 'dismissJsmGracePeriodBanner', {
							source: 'jsmGracePeriodBanner',
							bannerType,
							typeOfUserEngagement: 'high',
						});
						loadingRef.current = false;
					});
			}
		},
		[update, accountId],
	);

	useEffect(() => {
		if (!data && !loading && !error) {
			refresh();
		}
	}, [data, loading, refresh, error]);

	return useMemo(() => {
		// oxlint-disable-next-line react/refs
		if (errorRef.current || loading || !data || !accountId) {
			return {
				loading,
				displayState: {
					hideT7Banner: true,
					hideT30Banner: true,
					hideT90BannerNew: true,
				},
				dismissAnnouncement,
			};
		}

		return {
			displayState: data,
			loading,
			dismissAnnouncement,
		};
	}, [loading, data, accountId, dismissAnnouncement]);
};

export const jsmGracePeriodAnnouncementGpEndDateResource: RouteResource<GpEndDateData | undefined> =
	createResource<GpEndDateData | undefined>({
		type: 'JSM_GRACE_PERIOD_ANNOUNCEMENT_GP_END_DATE',
		getKey: () => 'JSM_GRACE_PERIOD_ANNOUNCEMENT_GP_END_DATE',
		getDataLoader: () =>
			import(
				/*
			webpackChunkName: "async-resource-jsm-grace-period-announcements-gp-end-date",
			jiraSpaEntry: "async-resource-jsm-gracae-period-announcements-gp-end-date"
		*/
				'./getGpEndDateData.tsx'
			),
		maxAge: Infinity,
		isPageShellCritical: true,
		isBrowserOnly: true,
	});

export const useJsmGracePeriodAnnouncementGpEndDateResource = ():
	| {
			loading: boolean;
			gpEndDateDetails?: undefined;
			gpEndDate?: undefined;
	  }
	| {
			gpEndDateDetails:
				| {
						maintenanceEndDate: Date;
						billingPeriod: 'MONTHLY' | 'ANNUAL';
				  }
				| undefined;
			gpEndDate: Date | undefined;
			loading: false;
	  } => {
	const { data, loading, error, refresh } = useResource(
		jsmGracePeriodAnnouncementGpEndDateResource,
	);
	const errorRef = useRef<unknown>();
	// oxlint-disable-next-line react/refs
	errorRef.current = error;
	const hasRefreshedRef = useRef(false);

	useEffect(() => {
		if (!data && !loading && !error) {
			// The loader resolves `undefined` for tenants with nothing to show: an ineligible
			// edition, no entitlement ID, or no maintenance date. Those resolutions leave the
			// resource with no data and no error, so without this guard the effect re-runs on
			// every resolution and refreshes forever.
			if (hasRefreshedRef.current && fg('jsm_gp_missing_end_date_graceful')) {
				return;
			}

			hasRefreshedRef.current = true;
			refresh();
		}
	}, [data, loading, refresh, error]);

	return useMemo(() => {
		// oxlint-disable-next-line react/refs
		if (errorRef.current || loading) {
			return {
				loading,
			};
		}

		return {
			gpEndDateDetails: data?.gpEndDateDetails,
			gpEndDate: data?.gracePeriodEndDate,
			loading,
		};
	}, [loading, data]);
};
